/**
 * Learning Loop Hook
 *
 * Provides functionality for the PREPARE → PRACTICE → REFLECT loop
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  findLinkedSimulations,
  findRelatedSimulations,
  extractContextFromCoachingSession,
  storeCoachingContextForSimulation,
  getDebriefTemplate,
  type CoachingContext,
  type LinkedSimulation
} from '@/lib/learningLoop/contextService';

export interface LearningLoopState {
  coachingSession?: any;
  coachingContext?: CoachingContext;
  linkedSimulations: LinkedSimulation[];
  relatedSimulations: LinkedSimulation[];
  debriefTemplate?: any;
  simulationEvaluation?: any;
}

/**
 * Hook to get linked simulations for a coaching session
 */
export function useLinkedSimulations(coachingTemplateId: string | undefined) {
  return useQuery({
    queryKey: ['linked-simulations', coachingTemplateId],
    queryFn: async () => {
      if (!coachingTemplateId) return [];
      return findLinkedSimulations(coachingTemplateId);
    },
    enabled: !!coachingTemplateId
  });
}

/**
 * Hook to get related simulations by category
 */
export function useRelatedSimulations(category: string, companyId?: string | null) {
  return useQuery({
    queryKey: ['related-simulations', category, companyId],
    queryFn: async () => {
      return findRelatedSimulations(category, companyId);
    },
    enabled: !!category
  });
}

/**
 * Hook to get a debrief template
 */
export function useDebriefTemplate(category: string = 'sales') {
  return useQuery({
    queryKey: ['debrief-template', category],
    queryFn: async () => {
      return getDebriefTemplate(category);
    }
  });
}

/**
 * Hook to manage the full learning loop state
 */
export function useLearningLoop(coachingSessionId?: string) {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();

  // Use profile from AuthContext (already synced to auth.uid())
  // Avoid re-querying profiles with a potentially mismatched ID


  // Get coaching session details
  const { data: coachingSession, isLoading: sessionLoading } = useQuery({
    queryKey: ['learning-loop-session', coachingSessionId],
    queryFn: async () => {
      if (!coachingSessionId) return null;

      const { data, error } = await supabase
        .from('coaching_sessions')
        .select(`
          *,
          coaching_templates (
            id,
            name,
            category,
            description
          )
        `)
        .eq('session_id', coachingSessionId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!coachingSessionId
  });

  // Extract context from coaching session
  const coachingContext = coachingSession
    ? extractContextFromCoachingSession(coachingSession)
    : undefined;

  const templateId = coachingSession?.coaching_templates?.id;
  const category = coachingSession?.coaching_templates?.category || 'sales';

  // Get linked simulations
  const { data: linkedSimulations = [], isLoading: linkedLoading } = useLinkedSimulations(templateId);

  // Get related simulations if no linked ones
  const { data: relatedSimulations = [], isLoading: relatedLoading } = useRelatedSimulations(
    category,
    profile?.company_id
  );

  // Get debrief template
  const { data: debriefTemplate, isLoading: debriefLoading } = useDebriefTemplate(category);

  // Store context and navigate to simulation
  const startPractice = useMutation({
    mutationFn: async (simulationId: string) => {
      if (!coachingContext) {
        throw new Error('No coaching context available');
      }

      // Store context for the simulation
      await storeCoachingContextForSimulation(coachingSessionId!, coachingContext);

      // Return the navigation target
      return {
        simulationId,
        coachingSessionId,
        contextStored: true
      };
    }
  });

  // Get simulations available for practice (linked first, then related)
  const availableSimulations = linkedSimulations.length > 0
    ? linkedSimulations
    : relatedSimulations;

  return {
    // State
    coachingSession,
    coachingContext,
    linkedSimulations,
    relatedSimulations,
    availableSimulations,
    debriefTemplate,

    // Loading states
    isLoading: sessionLoading || linkedLoading || relatedLoading || debriefLoading,

    // Actions
    startPractice: startPractice.mutateAsync,
    isPracticePending: startPractice.isPending,

    // Helpers
    hasLinkedSimulations: linkedSimulations.length > 0,
    isReadyToPractice: coachingSession?.status === 'completed' && coachingSession?.readiness_score >= 60
  };
}

/**
 * Hook to track session links (coaching -> simulation -> debrief)
 */
export function useSessionLinks(simulationSessionId?: string) {
  return useQuery({
    queryKey: ['session-links', simulationSessionId],
    queryFn: async () => {
      if (!simulationSessionId) return null;

      const { data: evaluation, error } = await supabase
        .from('simulation_evaluations')
        .select(`
          id,
          session_id,
          coaching_session_id,
          debrief_session_id,
          overall_score,
          detailed_evaluation
        `)
        .eq('session_id', simulationSessionId)
        .single();

      if (error) throw error;

      let coachingSession = null;
      let debriefSession = null;

      // Get linked coaching session if exists
      if (evaluation?.coaching_session_id) {
        const { data } = await supabase
          .from('coaching_sessions')
          .select('*, coaching_templates(name)')
          .eq('session_id', evaluation.coaching_session_id)
          .single();
        coachingSession = data;
      }

      // Get linked debrief session if exists
      if (evaluation?.debrief_session_id) {
        const { data } = await supabase
          .from('coaching_sessions')
          .select('*, coaching_templates(name)')
          .eq('session_id', evaluation.debrief_session_id)
          .single();
        debriefSession = data;
      }

      return {
        evaluation,
        coachingSession,
        debriefSession,
        hasCoaching: !!coachingSession,
        hasDebrief: !!debriefSession
      };
    },
    enabled: !!simulationSessionId
  });
}

/**
 * Hook to get user's learning pathway progress
 */
export function usePathwayProgress(pathwayId?: string) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['pathway-progress', pathwayId, profile?.id],
    queryFn: async () => {
      if (!pathwayId || !profile?.id) return null;

      // Get enrollment - use maybeSingle to avoid 406 errors
      const { data: enrollment, error } = await supabase
        .from('user_pathway_enrollments')
        .select(`
          *,
          learning_pathways (*),
          pathway_modules:current_module_id (*)
        `)
        .eq('user_id', profile.id)
        .eq('pathway_id', pathwayId)
        .maybeSingle();

      if (error) throw error;
      if (!enrollment) return null;

      // Get module progress
      const { data: moduleProgress } = await supabase
        .from('module_progress')
        .select(`
          *,
          pathway_modules (*)
        `)
        .eq('enrollment_id', enrollment.id)
        .order('pathway_modules(sequence_order)');

      return {
        enrollment,
        moduleProgress: moduleProgress || [],
        isCompleted: enrollment.status === 'completed',
        completionPercentage: (enrollment.modules_completed / (moduleProgress?.length || 1)) * 100
      };
    },
    enabled: !!pathwayId && !!profile?.id
  });
}

/**
 * Hook to enroll in a learning pathway
 */
export function useEnrollInPathway() {
  const { profile } = useAuth(); // Use profile directly from AuthContext
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pathwayId: string) => {
      if (!profile?.id) {
        throw new Error('User not authenticated');
      }

      // Ensure Supabase auth session exists (auth.uid() required for RLS)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated with Supabase. Please refresh and try again.');
      }

      // Create enrollment using profile from context
      const { data: enrollment, error: enrollError } = await supabase
        .from('user_pathway_enrollments')
        .insert({
          user_id: profile.id,
          pathway_id: pathwayId,
          company_id: user?.company_id ?? null,
          status: 'enrolled'
        })
        .select()
        .single();

      if (enrollError) throw enrollError;

      // Get pathway modules
      const { data: modules } = await supabase
        .from('pathway_modules')
        .select('id')
        .eq('pathway_id', pathwayId)
        .order('sequence_order');

      // Create module progress records
      if (modules && modules.length > 0) {
        const progressRecords = modules.map((module, index) => ({
          enrollment_id: enrollment.id,
          module_id: module.id,
          user_id: profile.id,
          status: index === 0 ? 'unlocked' : 'locked'
        }));

        await supabase.from('module_progress').insert(progressRecords);

        // Update current module to first module
        await supabase
          .from('user_pathway_enrollments')
          .update({ current_module_id: modules[0].id, status: 'in_progress' })
          .eq('id', enrollment.id);
      }

      return enrollment;
    },
    onSuccess: (_, pathwayId) => {
      queryClient.invalidateQueries({ queryKey: ['pathway-progress', pathwayId] });
      queryClient.invalidateQueries({ queryKey: ['user-pathways'] });
    }
  });
}
